
import { useState } from 'react'
import { Plus, X, Edit2, Clock } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { FoodSearchDialog } from './food-search-dialog'
import { CustomFoodForm } from './custom-food-form'
import type { Meal, FoodItem, MealType } from '@/types/diet-v2'

interface MealBuilderProps {
  meal: Omit<Meal, '_id' | 'totalCalories' | 'macros'>
  onChange: (meal: Omit<Meal, '_id' | 'totalCalories' | 'macros'>) => void
  onRemove?: () => void
}

export function MealBuilder({ meal, onChange, onRemove }: MealBuilderProps) {
  const [showFoodSearch, setShowFoodSearch] = useState(false)
  const [showCustomFoodForm, setShowCustomFoodForm] = useState(false)
  const [editingFoodIndex, setEditingFoodIndex] = useState<number | null>(null)

  // Calculate totals
  const totals = meal.foods.reduce(
    (acc, food) => ({
      calories: acc.calories + food.calories,
      protein: acc.protein + food.macros.protein,
      carbs: acc.carbs + food.macros.carbs,
      fat: acc.fat + food.macros.fat,
      fiber: acc.fiber + (food.macros.fiber || 0),
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 }
  )

  const handleAddFood = (food: FoodItem) => {
    onChange({
      ...meal,
      foods: [...meal.foods, food],
    })
  }

  const handleRemoveFood = (index: number) => {
    onChange({
      ...meal,
      foods: meal.foods.filter((_, i) => i !== index),
    })
  }

  const handleUpdateFoodServing = (index: number, newServing: number) => {
    const food = meal.foods[index]
    const ratio = newServing / food.servingSize

    const updatedFood: FoodItem = {
      ...food,
      servingSize: newServing,
      calories: Math.round(food.calories * ratio),
      macros: {
        protein: Number((food.macros.protein * ratio).toFixed(1)),
        carbs: Number((food.macros.carbs * ratio).toFixed(1)),
        fat: Number((food.macros.fat * ratio).toFixed(1)),
        fiber: food.macros.fiber ? Number((food.macros.fiber * ratio).toFixed(1)) : undefined,
      },
    }

    onChange({
      ...meal,
      foods: meal.foods.map((f, i) => (i === index ? updatedFood : f)),
    })
  }

  const mealTypeNames: Record<MealType, string> = {
    breakfast: 'Breakfast',
    lunch: 'Lunch',
    dinner: 'Dinner',
    snack: 'Snack',
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{mealTypeNames[meal.type]}</CardTitle>
            {onRemove && (
              <Button variant="ghost" size="sm" onClick={onRemove}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <Input
              type="time"
              value={meal.time || ''}
              onChange={(e) => onChange({ ...meal, time: e.target.value })}
              className="w-32"
            />
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Foods List */}
          {meal.foods.length > 0 ? (
            <div className="space-y-3">
              {meal.foods.map((food, index) => (
                <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{food.name}</span>
                      <Badge variant={food.source === 'usda' ? 'default' : food.source === 'ai' ? 'outline' : 'secondary'} className="text-xs">
                        {food.source === 'usda' ? 'USDA' : food.source === 'ai' ? 'AI' : 'Custom'}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {food.calories} cal • P: {food.macros.protein}g • C: {food.macros.carbs}g • F: {food.macros.fat}g
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.1"
                      value={food.servingSize}
                      onChange={(e) => handleUpdateFoodServing(index, parseFloat(e.target.value) || 0)}
                      className="w-20"
                    />
                    <span className="text-sm text-muted-foreground">{food.servingUnit}</span>
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveFood(index)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              <p>No foods added yet</p>
            </div>
          )}

          {/* Add Food Button */}
          <Button variant="outline" className="w-full" onClick={() => setShowFoodSearch(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Food
          </Button>

          {/* Meal Notes */}
          <div className="space-y-2">
            <Label htmlFor={`notes-${meal.type}`}>Notes (optional)</Label>
            <Input
              id={`notes-${meal.type}`}
              value={meal.notes || ''}
              onChange={(e) => onChange({ ...meal, notes: e.target.value })}
              placeholder="e.g., Post-workout meal"
            />
          </div>

          <Separator />

          {/* Totals */}
          <div className="bg-muted/50 p-4 rounded-lg">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold">{totals.calories}</div>
                <div className="text-xs text-muted-foreground">Calories</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{totals.protein.toFixed(1)}g</div>
                <div className="text-xs text-muted-foreground">Protein</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{totals.carbs.toFixed(1)}g</div>
                <div className="text-xs text-muted-foreground">Carbs</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{totals.fat.toFixed(1)}g</div>
                <div className="text-xs text-muted-foreground">Fat</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{totals.fiber.toFixed(1)}g</div>
                <div className="text-xs text-muted-foreground">Fiber</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Food Search Dialog */}
      <FoodSearchDialog
        open={showFoodSearch}
        onOpenChange={setShowFoodSearch}
        onSelectFood={handleAddFood}
        onCreateCustomFood={() => {
          setShowFoodSearch(false)
          setShowCustomFoodForm(true)
        }}
      />

      {/* Custom Food Form */}
      <CustomFoodForm
        open={showCustomFoodForm}
        onOpenChange={setShowCustomFoodForm}
        onSuccess={() => {
          setShowCustomFoodForm(false)
          setShowFoodSearch(true)
        }}
      />
    </>
  )
}
